import { useState } from "react";

import dayjs from "dayjs";
import "dayjs/locale/es";
import { PackageCheck } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { TablePagination } from "~/components/ui/table-pagination";
import { usePagination } from "~/hooks/usePagination";
import { TotemPendingWithRelations } from "~/services/totemService";

import { ResolvePendingDialog } from "./ResolvePendingDialog";
import { usePendingAssignments } from "../hooks/usePendingAssignments";

dayjs.locale("es");

const PAGE_SIZE = 15;

interface PendingAssignmentsTabProps {
  // Totems currently in "Disponible" (available + filled) — forwarded to the resolve dialog.
  availableCount: number;
}

export function PendingAssignmentsTab({
  availableCount,
}: PendingAssignmentsTabProps) {
  const [scope, setScope] = useState<"pending" | "all">("pending");
  const { data: rows, isLoading } = usePendingAssignments(scope === "all");
  const [selected, setSelected] = useState<TotemPendingWithRelations | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const { page, setPage, pageCount, pageItems, total } = usePagination(
    rows ?? [],
    PAGE_SIZE
  );

  const openResolve = (row: TotemPendingWithRelations) => {
    setSelected(row);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Mostrar</label>
          <Select
            value={scope}
            onValueChange={(v) => setScope(v as "pending" | "all")}
          >
            <SelectTrigger className="h-9 w-full sm:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Solo pendientes</SelectItem>
              <SelectItem value="all">Todos (incluye resueltos)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4">
              <TableSkeleton rows={6} columns={8} />
            </div>
          ) : (rows ?? []).length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={PackageCheck}
                title="No hay asignaciones pendientes"
                description="Cuando se facturen tótems y no haya disponibles, el faltante aparecerá aquí para moverlo al cliente."
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Fecha</TableHead>
                      <TableHead>Factura</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Producto</TableHead>
                      <TableHead className="text-right">Requeridos</TableHead>
                      <TableHead className="text-right">Asignados</TableHead>
                      <TableHead className="text-right">Pendientes</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acción</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageItems.map((row) => (
                      <TableRow key={row.id} className="hover:bg-muted/30">
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {dayjs(row.created_at).format("DD MMM YYYY, HH:mm")}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.bsale_number ?? "—"}
                        </TableCell>
                        <TableCell>{row.client?.name ?? "—"}</TableCell>
                        <TableCell className="text-sm text-gray-700">
                          {row.product?.short_name ?? "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.required_count}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.assigned_count}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-700">
                          {row.pending_count}
                        </TableCell>
                        <TableCell>
                          {row.status === "pending" ? (
                            <Badge variant="warning">Pendiente</Badge>
                          ) : (
                            <Badge variant="secondary">Resuelto</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.status === "pending" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => openResolve(row)}
                            >
                              Resolver
                            </Button>
                          ) : (
                            <span className="text-sm text-muted-foreground">
                              {row.resolved_at
                                ? dayjs(row.resolved_at).format("DD MMM YYYY")
                                : "—"}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {pageCount > 1 && (
                <div className="border-t p-4">
                  <TablePagination
                    page={page}
                    pageCount={pageCount}
                    totalItems={total}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                    itemLabel="pendientes"
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ResolvePendingDialog
        pending={selected}
        availableCount={availableCount}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
}
